var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { createSelector } from '@reduxjs/toolkit';
import { chain, get, keyBy, merge, pick } from 'lodash';
import { useMemo } from 'react';
import { stringToNumber } from '../common/numberInput/hooks';
import { WizardStep } from './constants';
import { useQueueAPI } from './queue/selectors';
import { useWizardParams } from './selectors';
import { OVERALL_TIME, getTimes, selectQueueDetail } from './workArrivalPattern/selectors';
var selectWAVMap = createSelector(selectQueueDetail, function (queue) {
  return keyBy(queue === null || queue === void 0 ? void 0 : queue.wapVolumes, function (v) {
    return "".concat(v.month, "-").concat(v.day, "-").concat(v.time);
  });
});
var selectDailyVolumeValues = createSelector(selectQueueDetail, selectWAVMap, function (queue, volume) {
  return chain(queue === null || queue === void 0 ? void 0 : queue.wapTimes).map(function (_a) {
    var month = _a.month,
      day = _a.day,
      openTime = _a.openTime,
      closeTime = _a.closeTime;
    return getTimes(queue === null || queue === void 0 ? void 0 : queue.timeInterval, openTime, closeTime !== null && closeTime !== void 0 ? closeTime : undefined).map(function (time) {
      var _a, _b;
      return {
        month: month,
        day: day,
        time: time,
        workVolume: stringToNumber((_b = (_a = volume["".concat(month, "-").concat(day, "-").concat(time)]) === null || _a === void 0 ? void 0 : _a.workVolume) !== null && _b !== void 0 ? _b : undefined)
      };
    });
  }).flatten().groupBy(function (_a) {
    var month = _a.month;
    return "month".concat(month);
  }).mapValues(function (arr) {
    return chain(arr).groupBy(function (i) {
      return i.time === OVERALL_TIME ? OVERALL_TIME : "day".concat(i.day);
    }).values().value();
  }).value();
});
export var pickNumberValues = function (queue, pickFields) {
  var pickToNumber = [];
  for (var _i = 2; _i < arguments.length; _i++) {
    pickToNumber[_i - 2] = arguments[_i];
  }
  var result = {};
  pickToNumber.forEach(function (field) {
    var _a;
    result[field] = stringToNumber((_a = get(queue, field)) === null || _a === void 0 ? void 0 : _a.toString());
  });
  return __assign(__assign({}, pick.apply(void 0, __spreadArray([queue], pickFields, false))), result);
};
export var getDefaultVolumes = function (data) {
  var _a, _b, _c;
  var fields = [];
  for (var _i = 1; _i < arguments.length; _i++) {
    fields[_i - 1] = arguments[_i];
  }
  var result = {
    volumes: (_b = (_a = data === null || data === void 0 ? void 0 : data.volumes) === null || _a === void 0 ? void 0 : _a.map(function (volume) {
      return __assign({}, pickNumberValues.apply(void 0, __spreadArray([volume, ['id']], fields, false)));
    })) !== null && _b !== void 0 ? _b : []
  };
  if ((_c = data === null || data === void 0 ? void 0 : data.queueLocations) === null || _c === void 0 ? void 0 : _c.length) {
    result.queueLocations = data.queueLocations.map(function (location) {
      return {
        id: location.id,
        planLocationId: location.planLocation.id,
        volumes: location.volumes.map(function (volume) {
          return __assign({}, pickNumberValues.apply(void 0, __spreadArray([volume, ['id']], fields, false)));
        })
      };
    });
  }
  if (fields.includes('volume') || fields.includes('productionHour')) {
    result.volumeEntryMode = data === null || data === void 0 ? void 0 : data.volumeEntryMode;
  }
  if (fields.includes('attrition')) {
    result.attritionEntryMode = data === null || data === void 0 ? void 0 : data.attritionEntryMode;
  }
  return result;
};
var selectVolumeValues = createSelector(selectQueueDetail, selectDailyVolumeValues, function (queue, wapVolumes) {
  return __assign(__assign({}, pickNumberValues(queue, ['answered', 'threshold', 'factor', 'metricTarget', 'targetAsa', 'timezone'], 'maxOccupancy', 'targetOccupancy', 'chatConcurrency', 'patience', 'retryRate')), {
    wapVolumes: wapVolumes
  });
});
export var selectShrinkageValues = createSelector(selectQueueDetail, function (queue) {
  var _a, _b, _c;
  var allShrinkages = __spreadArray(__spreadArray([], (_a = queue === null || queue === void 0 ? void 0 : queue.shrinkages) !== null && _a !== void 0 ? _a : [], true), (_c = (_b = queue === null || queue === void 0 ? void 0 : queue.queueLocations) === null || _b === void 0 ? void 0 : _b.flatMap(function (location) {
    var _a;
    return (_a = location.shrinkages) !== null && _a !== void 0 ? _a : [];
  })) !== null && _c !== void 0 ? _c : [], true);
  return {
    shrinkages: chain(allShrinkages).groupBy('month').mapValues(function (arr) {
      return arr.map(function (_a) {
        var id = _a.id,
          month = _a.month,
          name = _a.name,
          percent = _a.percent,
          queueLocationId = _a.queueLocationId,
          shrinkageCategory = _a.shrinkageCategory,
          shrinkageSubcategory = _a.shrinkageSubcategory,
          shrinkageType = _a.shrinkageType;
        return {
          id: id,
          month: month,
          name: name,
          percent: Number(percent),
          queueLocationId: queueLocationId,
          shrinkageCategory: shrinkageCategory,
          shrinkageSubcategory: shrinkageSubcategory,
          shrinkageType: shrinkageType
        };
      });
    }).values().value()
  };
});
var selectWorkingDayValues = createSelector(selectQueueDetail, function (queue) {
  var _a, _b, _c, _d;
  return __assign(__assign({}, pickNumberValues(queue, ['timeInterval'], 'fteDay', 'totalPartTime', 'dailyOperatingHours')), {
    daysPerWeek: (_b = stringToNumber((_a = queue === null || queue === void 0 ? void 0 : queue.daysPerWeek) === null || _a === void 0 ? void 0 : _a.toString())) !== null && _b !== void 0 ? _b : 0,
    hoursPerDay: (_d = stringToNumber((_c = queue === null || queue === void 0 ? void 0 : queue.hoursPerDay) === null || _c === void 0 ? void 0 : _c.toString())) !== null && _d !== void 0 ? _d : 0
  });
});
var selectWorkingDayLocationValues = createSelector(selectQueueDetail, function (queue) {
  var _a;
  if (!((_a = queue === null || queue === void 0 ? void 0 : queue.queueLocations) === null || _a === void 0 ? void 0 : _a.length)) return {};
  return {
    queueLocations: queue.queueLocations.map(function (location) {
      var _a, _b, _c, _d, _e, _f, _g;
      return {
        id: location.id,
        fteDay: (_b = stringToNumber((_a = location.fteDay) === null || _a === void 0 ? void 0 : _a.toString())) !== null && _b !== void 0 ? _b : null,
        totalPartTime: (_d = stringToNumber((_c = location.totalPartTime) === null || _c === void 0 ? void 0 : _c.toString())) !== null && _d !== void 0 ? _d : 0,
        hoursPerDay: (_f = stringToNumber((_e = location.hoursPerDay) === null || _e === void 0 ? void 0 : _e.toString())) !== null && _f !== void 0 ? _f : 0,
        daysPerWeek: (_g = location.daysPerWeek) !== null && _g !== void 0 ? _g : 0
      };
    })
  };
});
export var selectSupportStaffValues = createSelector(selectQueueDetail, function (queue) {
  var _a, _b, _c;
  if ((_a = queue === null || queue === void 0 ? void 0 : queue.queueLocations) === null || _a === void 0 ? void 0 : _a.length) {
    return {
      queueLocations: ((_b = queue === null || queue === void 0 ? void 0 : queue.queueLocations) === null || _b === void 0 ? void 0 : _b.map(function (location) {
        var _a, _b, _c;
        return {
          id: location.id,
          roles: (_a = location === null || location === void 0 ? void 0 : location.roles) === null || _a === void 0 ? void 0 : _a.map(function (role) {
            return pickNumberValues(role, ['id', 'staffCalculate', 'role', 'roleAppliesTo', 'wageType'], 'ratio', 'salary', 'loadFactor');
          }),
          currencyCode: (location === null || location === void 0 ? void 0 : location.currencyCode) || 'USD',
          multiplier: Number((_b = location === null || location === void 0 ? void 0 : location.multiplier) !== null && _b !== void 0 ? _b : 1),
          trainWeeks: Number((_c = location === null || location === void 0 ? void 0 : location.trainWeeks) !== null && _c !== void 0 ? _c : 0)
        };
      })) || []
    };
  }
  return {
    roles: (_c = queue === null || queue === void 0 ? void 0 : queue.roles) === null || _c === void 0 ? void 0 : _c.map(function (item) {
      return pickNumberValues(item, ['id', 'staffCalculate', 'role', 'roleAppliesTo', 'wageType'], 'ratio', 'salary', 'loadFactor');
    })
  };
});
var createSelectorFinancialValues = function () {
  return createSelector(selectQueueDetail, function (queue) {
    var _a;
    var hasLocations = !!((_a = queue === null || queue === void 0 ? void 0 : queue.queueLocations) === null || _a === void 0 ? void 0 : _a.length);
    return __assign(__assign(__assign({}, selectSupportStaffValues(queue)), pickNumberValues(queue, ['trainWeeks'], 'multiplier')), {
      sourceCurrencyCode: hasLocations ? undefined : queue === null || queue === void 0 ? void 0 : queue.currencyCode
    });
  });
};
export var useDefaultValues = function () {
  var queueData = useQueueAPI()[0];
  var step = useWizardParams().step;
  var selectFinancialValues = useMemo(function () {
    return createSelectorFinancialValues();
  }, []);
  return useMemo(function () {
    var _a;
    var _b, _c;
    var defaultValue = (_a = {}, _a[WizardStep.WorkVolume] = function () {
      return __assign(__assign({}, selectVolumeValues(queueData.data)), getDefaultVolumes(queueData.data, 'volume'));
    }, _a[WizardStep.ProdHours] = function () {
      return __assign(__assign({}, selectVolumeValues(queueData.data)), getDefaultVolumes(queueData.data, 'productionHour', 'prodOccupancy'));
    }, _a[WizardStep.HandleTime] = function () {
      var _a, _b;
      return __assign(__assign({}, getDefaultVolumes(queueData.data, 'handleTime', 'learnCurve')), {
        attritionEntryMode: (_b = (_a = queueData.data) === null || _a === void 0 ? void 0 : _a.attritionEntryMode) !== null && _b !== void 0 ? _b : 'percentage'
      });
    }, _a[WizardStep.Shrinkage] = function () {
      return selectShrinkageValues(queueData.data);
    }, _a[WizardStep.Attrition] = function () {
      var _a, _b;
      return __assign(__assign({}, getDefaultVolumes(queueData.data, 'attrition')), {
        attritionEntryMode: (_b = (_a = queueData.data) === null || _a === void 0 ? void 0 : _a.attritionEntryMode) !== null && _b !== void 0 ? _b : 'percentage'
      });
    }, _a[WizardStep.WorkingDays] = function () {
      var _a, _b, _c;
      return merge({}, selectWorkingDayValues(queueData.data), selectWorkingDayLocationValues(queueData.data), {
        volumes: (_c = (_b = (_a = queueData.data) === null || _a === void 0 ? void 0 : _a.volumes) === null || _b === void 0 ? void 0 : _b.map(function (v) {
          return pickNumberValues(v, ['id'], 'workingDays');
        })) !== null && _c !== void 0 ? _c : []
      });
    }, _a[WizardStep.SupportStaff] = function () {
      return selectSupportStaffValues(queueData.data);
    }, _a[WizardStep.Financial] = function () {
      return merge({}, selectFinancialValues(queueData.data), getDefaultVolumes(queueData.data, 'rate'));
    }, _a[WizardStep.Review] = function () {
      var _a, _b;
      return merge({}, __assign(__assign(__assign(__assign(__assign({}, selectVolumeValues(queueData.data)), selectShrinkageValues(queueData.data)), selectWorkingDayValues(queueData.data)), selectFinancialValues(queueData.data)), {
        attritionEntryMode: (_b = (_a = queueData.data) === null || _a === void 0 ? void 0 : _a.attritionEntryMode) !== null && _b !== void 0 ? _b : 'percentage'
      }), selectWorkingDayLocationValues(queueData.data), getDefaultVolumes(queueData.data, 'volume', 'handleTime', 'learnCurve', 'attrition', 'workingDays', 'rate', 'productionHour', 'prodOccupancy'));
    }, _a);
    return __assign({
      id: (_b = queueData.data) === null || _b === void 0 ? void 0 : _b.id
    }, (_c = defaultValue[step]) === null || _c === void 0 ? void 0 : _c.call(defaultValue));
  }, [queueData.data, selectFinancialValues, step]);
};